import { useContext, useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import styles from '../van-down-modal/styles.module.css'
import { ToastNotificationContext } from '../../../global/context/toast-context/ToastNotificationContext'
import { fetchData } from '../../../global/utils/fetch'
import PrimaryButton from '../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../global/components/buttons/secondary-button/SecondaryButton'
import {
  DEFAULT_USER,
  OPERATING_STATUSES,
  ISSUES_STATUSES_ARRAY,
} from '../../../global/constants/van'
import { UserContext } from '../../../base/components/base-container/BaseContainer'
import { IVan } from '../../../../../app/entities/Van'

interface IReportVanDownModalProps {
  isOpen: boolean
  onClose: () => void
  refetchTable: () => void
}

export default function ReportVanDownModal(props: IReportVanDownModalProps) {
  const { isOpen, onClose, refetchTable } = props
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)
  const { user } = useContext(UserContext)

  // Van selection state
  const [vans, setVans] = useState<Partial<IVan>[]>([])
  const [selectedVan, setSelectedVan] = useState<Partial<IVan> | null>(null)
  const [isLoadingVans, setIsLoadingVans] = useState(false)

  // Form state
  const [statusDate, setStatusDate] = useState(
    new Date().toISOString().split('T')[0],
  )
  const [issue, setIssue] = useState('')
  const [note, setNote] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    async function fetchVans() {
      if (isOpen) {
        setIsLoadingVans(true)
        try {
          // Fetch all vans that are not already inoperative
          const response = await fetchData<{ items: Partial<IVan>[]; count: number }>({
            url: `${process.env.REACT_APP_ORIGIN}/vans?operatingStatus=${OPERATING_STATUSES.ACTIVE}&operatingStatus=${OPERATING_STATUSES.STANDBY}`,
            method: 'GET',
          })
          // Sort vans alphabetically by location name, then by van name
          const sortedVans = (response?.items || []).sort((a, b) => {
            const locationA = a.location?.name || ''
            const locationB = b.location?.name || ''
            if (locationA !== locationB) {
              return locationA.localeCompare(locationB)
            }
            return (a.name || '').localeCompare(b.name || '')
          })
          setVans(sortedVans)
        } catch (error) {
          console.error('Error fetching vans:', error)
          setToastStatus({
            ...toastStatus,
            isOpen: true,
            message: 'Error loading vans',
            severity: 'error',
          })
        } finally {
          setIsLoadingVans(false)
        }
      }
    }

    if (isOpen) {
      // Reset state
      setSelectedVan(null)
      setStatusDate(new Date().toISOString().split('T')[0])
      setIssue('')
      setNote('')
      fetchVans()
    }
  }, [isOpen])

  async function handleSubmit() {
    if (!selectedVan) return

    if (!issue) {
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Issue is required when reporting a van as inoperative',
        severity: 'error',
      })
      return
    }

    setIsSubmitting(true)
    try {
      // Backend uses 'note' field for the issue type
      // Combine issue type with additional notes if provided
      const issueNote = note ? `${issue} - ${note}` : issue

      await fetchData({
        url: `${process.env.REACT_APP_ORIGIN}/vans/${selectedVan.objectId}/operating-status`,
        method: 'PUT',
        body: {
          operatingStatus: OPERATING_STATUSES.INOPERATIVE,
          operatingStatusStartDate: new Date(statusDate),
          note: issueNote,
          changedBy: user?.name || user?.email || DEFAULT_USER,
        },
      })

      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: `Van ${selectedVan.name} reported as inoperative`,
        severity: 'success',
      })

      refetchTable()
      onClose()
    } catch (error) {
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: 'Error reporting van as inoperative',
        severity: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const isFormValid = selectedVan && issue && statusDate && note.trim()

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
    >
      <Box className={styles.vanDownModal}>
        <div className={styles.header}>
          <h2>Report Van Down</h2>
          <h3>Report a van as inoperative</h3>
        </div>

        <div className={styles.content}>
          {/* Van Selector */}
          <TextField
            select
            label='Select Van'
            value={selectedVan?.objectId || ''}
            onChange={(e) => {
              const van = vans.find((v) => v.objectId === e.target.value)
              setSelectedVan(van || null)
            }}
            fullWidth
            required
            disabled={isLoadingVans}
            sx={{ marginBottom: '16px' }}
          >
            {isLoadingVans ? (
              <MenuItem value='' disabled>
                Loading vans...
              </MenuItem>
            ) : vans.length === 0 ? (
              <MenuItem value='' disabled>
                No available vans
              </MenuItem>
            ) : (
              vans.map((van) => (
                <MenuItem key={van.objectId} value={van.objectId}>
                  {van.name}
                  {van.location?.name ? ` - ${van.location.name}` : ''}
                </MenuItem>
              ))
            )}
          </TextField>

          {/* Form Fields */}
          <TextField
            label='Effective Date'
            type='date'
            value={statusDate}
            onChange={(e) => setStatusDate(e.target.value)}
            fullWidth
            margin='normal'
            InputLabelProps={{ shrink: true }}
            required
          />

          <TextField
            select
            label='Issue'
            value={issue}
            onChange={(e) => setIssue(e.target.value)}
            fullWidth
            margin='normal'
            required
          >
            {ISSUES_STATUSES_ARRAY.map((issueOption) => (
              <MenuItem
                key={issueOption}
                value={issueOption}
              >
                {issueOption}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label='Notes'
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
            rows={3}
            margin='normal'
            placeholder='Additional details about the issue...'
            required
          />
        </div>

        <div className={styles.actions}>
          <SecondaryButton
            buttonName='Cancel'
            onClick={onClose}
            disabled={isSubmitting}
          />
          <PrimaryButton
            buttonName={isSubmitting ? 'Reporting...' : 'Report Van Down'}
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
          />
        </div>
      </Box>
    </Modal>
  )
}
